import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Briefcase, Copy, Crown, Link2, Search, Unlink, User, type LucideIcon } from 'lucide-react';

import PageHeader from '@/components/PageHeader';
import ManagerAuditPanel from '@/components/ManagerAuditPanel';
import {
  assignTenantReferrer,
  createManagerPayout,
  getManager,
  listTenants,
  updateManager,
  type ManagerDetail as ManagerDto,
  type ManagerRole,
  type TenantListItem,
} from '@/lib/operatorApi';
import { formatDate, formatKop } from '@/lib/format';

const ROLE_OPTIONS: Array<{ value: ManagerRole; label: string; hint: string; icon: LucideIcon }> = [
  {
    value: 'MANAGER',
    label: 'Менеджер',
    icon: User,
    hint: 'Видит только привязанные к нему заведения, оплаты и свою комиссию. Есть общий чат отдела.',
  },
  {
    value: 'HEAD_MANAGER',
    label: 'Главный менеджер',
    icon: Crown,
    hint: 'Все заведения платформы, добавляет менеджеров, принимает наличные, продлевает триал и дни. Каждое действие — в аудите ниже.',
  },
  {
    value: 'SALES_DIRECTOR',
    label: 'Руководитель отдела продаж',
    icon: Briefcase,
    hint: 'Всё, что может главный, плюс оборот компании, сейф отдела, расходы и рейтинг менеджеров. Добавляет главных менеджеров.',
  },
];

/** Что стало с заведениями удалённого — из журнала кабинета. */
function deletionSummary(d: ManagerDto['deletion']): string {
  const n = d?.establishments;
  if (n == null) return '';
  if (n === 0) return 'Заведений у него не было. ';
  return d?.transferTo ? `Заведений: ${n}, переданы ${d.transferTo.name}. ` : `Заведений: ${n}, остались без менеджера. `;
}

export default function ManagerDetail() {
  const { id } = useParams<{ id: string }>();
  const [mgr, setMgr] = useState<ManagerDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [payoutMethod, setPayoutMethod] = useState<'cash' | 'transfer' | 'other'>('cash');
  const [payoutComment, setPayoutComment] = useState('');
  const [payoutRub, setPayoutRub] = useState('');
  const [busy, setBusy] = useState(false);
  const [commissionPct, setCommissionPct] = useState('');
  const [note, setNote] = useState('');
  const [tenantQ, setTenantQ] = useState('');
  const [tenantHits, setTenantHits] = useState<TenantListItem[]>([]);
  const [tenantSearching, setTenantSearching] = useState(false);

  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    getManager(id)
      .then((m) => {
        setMgr(m);
        setCommissionPct((m.commissionBps / 100).toString());
        setNote(m.note ?? '');
      })
      .catch((err) => {
        setMgr(null);
        setLoadError(err instanceof Error ? err.message : 'Не удалось загрузить менеджера');
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = tenantQ.trim();
    if (q.length < 2) {
      setTenantHits([]);
      return;
    }
    const t = window.setTimeout(() => {
      setTenantSearching(true);
      listTenants({ q, limit: 12 })
        .then((r) => setTenantHits(r.results))
        .catch(() => setTenantHits([]))
        .finally(() => setTenantSearching(false));
    }, 250);
    return () => window.clearTimeout(t);
  }, [tenantQ]);

  const saveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    const pct = Number(commissionPct.replace(',', '.'));
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      window.alert('Комиссия: 0–100%');
      return;
    }
    try {
      setBusy(true);
      await updateManager(id, {
        commissionBps: Math.round(pct * 100),
        note: note.trim() || null,
      });
      load();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = () => {
    if (!mgr?.referralLink) return;
    void navigator.clipboard.writeText(mgr.referralLink);
  };

  const toggleBlock = async () => {
    if (!mgr || !id) return;
    const next = mgr.status === 'active' ? 'BLOCKED' : 'ACTIVE';
    const text =
      next === 'BLOCKED'
        ? 'Заблокировать менеджера? Вход и открытые вкладки кабинета закроются сразу.'
        : mgr.deletedAt
          ? `Восстановить ${mgr.name}? Он снова сможет войти и появится в команде. Заведения, переданные при удалении, не вернутся — привяжите их вручную.`
          : 'Разблокировать?';
    if (!window.confirm(text)) return;
    try {
      setBusy(true);
      await updateManager(id, { status: next });
      load();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (next: ManagerRole) => {
    if (!mgr || !id || next === mgr.role) return;
    const option = ROLE_OPTIONS.find((o) => o.value === next)!;
    const text = `${mgr.name}: роль «${option.label}»?\n\n${option.hint}\n\nДействует сразу, без перевхода.`;
    if (!window.confirm(text)) return;
    try {
      setBusy(true);
      await updateManager(id, { role: next });
      load();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  const attachTenant = async (tenant: TenantListItem) => {
    if (!id || !mgr) return;
    if (mgr.establishments.some((e) => e.id === tenant.id)) {
      window.alert('Уже привязано к этому менеджеру');
      return;
    }
    const warn = tenant.referrer
      ? `\n\nСейчас заведение привязано к ${tenant.referrer.name} (${tenant.referrer.code}) — привязка перейдёт.`
      : '';
    if (!window.confirm(`Привязать «${tenant.name}» к ${mgr.name}?${warn}`)) return;
    try {
      setBusy(true);
      await assignTenantReferrer(tenant.id, id);
      setTenantQ('');
      setTenantHits([]);
      load();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  const detachTenant = async (tenantId: string, name: string) => {
    if (!window.confirm(`Отвязать «${name}» от менеджера?`)) return;
    try {
      setBusy(true);
      await assignTenantReferrer(tenantId, null);
      load();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  const onPayout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !mgr) return;
    const rub = payoutRub.trim() ? Number(payoutRub.replace(',', '.')) : null;
    if (rub != null && (!Number.isFinite(rub) || rub <= 0)) {
      window.alert('Сумма должна быть > 0');
      return;
    }
    const amountKopecks = rub != null ? Math.round(rub * 100) : undefined;
    const label =
      payoutMethod === 'cash' ? 'наличкой' : payoutMethod === 'transfer' ? 'переводом' : 'иначе';
    const sumLabel = amountKopecks != null ? formatKop(amountKopecks) : formatKop(mgr.pendingKopecks);
    if (!window.confirm(`Отметить выплату ${sumLabel} ${label}?`)) return;
    try {
      setBusy(true);
      await createManagerPayout(id, {
        method: payoutMethod,
        amountKopecks,
        comment: payoutComment.trim() || null,
      });
      setPayoutComment('');
      setPayoutRub('');
      load();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="page-pad text-ink/50">Загрузка…</div>;
  if (loadError || !mgr) {
    return (
      <div className="page-pad space-y-3">
        <div className="text-ink/70">{loadError ?? 'Менеджер не найден'}</div>
        <Link to="/managers" className="btn-ghost inline-flex">
          <ArrowLeft size={14} /> К списку
        </Link>
      </div>
    );
  }

  const attachedIds = new Set(mgr.establishments.map((e) => e.id));
  const currentRole = ROLE_OPTIONS.find((o) => o.value === mgr.role) ?? ROLE_OPTIONS[0]!;

  return (
    <div>
      <PageHeader
        eyebrow={currentRole.label}
        title={mgr.name}
        description={[
          mgr.email,
          `код ${mgr.code}`,
          mgr.createdByManager ? `добавил ${mgr.createdByManager.name}` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <Link to="/managers" className="btn-ghost">
            <ArrowLeft size={14} /> К списку
          </Link>
        }
      />

      <div className="page-pad grid gap-4 lg:grid-cols-2">
        {mgr.deletedAt ? (
          <div className="card lg:col-span-2 flex flex-col gap-3 border-red-600/30 bg-red-50/60 p-4 md:flex-row md:items-center md:justify-between">
            <div className="text-sm">
              <div className="font-medium text-red-700">
                Удалён из команды {formatDate(mgr.deletedAt)}
                {mgr.deletion?.by ? ` · удалил ${mgr.deletion.by.name}` : ''}
              </div>
              <p className="mt-0.5 text-[12px] text-ink/60">
                {deletionSummary(mgr.deletion)}Вход закрыт, в кабинете отдела его нет. Начисления и выплаты сохранены —
                остаток к выплате можно закрыть ниже.
              </p>
            </div>
            <button type="button" className="btn-outline shrink-0" disabled={busy} onClick={() => void toggleBlock()}>
              Восстановить
            </button>
          </div>
        ) : null}

        <div className="card lg:col-span-2 p-4">
          <div className="text-sm font-medium">Роль в кабинете отдела продаж</div>
          <div className="mt-3 grid gap-2 md:grid-cols-3" role="radiogroup" aria-label="Роль в кабинете">
            {ROLE_OPTIONS.map((o) => {
              const active = mgr.role === o.value;
              const Icon = o.icon;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={busy}
                  onClick={() => void changeRole(o.value)}
                  className={
                    'border p-3 text-left transition disabled:opacity-60 ' +
                    (active ? 'border-orange bg-orange/5' : 'border-line bg-white hover:border-ink/40')
                  }
                >
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    <Icon size={14} strokeWidth={1.75} className={active ? 'text-orange' : 'text-ink/40'} />
                    {o.label}
                    {active ? <span className="badge-orange ml-auto">сейчас</span> : null}
                  </div>
                  <p className="mt-1 text-[12px] leading-relaxed text-ink/55">{o.hint}</p>
                </button>
              );
            })}
          </div>
        </div>

        <form className="card p-4 space-y-3" onSubmit={saveSettings}>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-ink/45 text-xs">К выплате</div>
              <div className="text-xl font-semibold">{formatKop(mgr.pendingKopecks)}</div>
            </div>
            <div>
              <div className="text-ink/45 text-xs">Начислено всего</div>
              <div className="text-xl font-semibold">{formatKop(mgr.earnedKopecks)}</div>
            </div>
          </div>
          <div>
            <label className="label">Комиссия с подписки, %</label>
            <input
              className="input mt-1"
              type="number"
              min={0}
              max={100}
              step={0.1}
              value={commissionPct}
              onChange={(e) => setCommissionPct(e.target.value)}
            />
            <p className="mt-1 text-[11px] text-ink/45">
              С каждого успешного платежа подписки привязанных заведений. Смена применяется к уже привязанным точкам (прошлые начисления не пересчитываются).
            </p>
          </div>
          <div>
            <label className="label">Заметка</label>
            <textarea className="input mt-1 min-h-[72px]" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <input className="input text-xs flex-1" readOnly value={mgr.referralLink} />
            <button type="button" className="btn-ghost" onClick={copyLink}><Copy size={14} /></button>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex-1" disabled={busy}>Сохранить ставку</button>
            <button type="button" className="btn-ghost" disabled={busy} onClick={() => void toggleBlock()}>
              {mgr.status === 'active' ? 'Заблокировать' : mgr.deletedAt ? 'Восстановить' : 'Разблокировать'}
            </button>
          </div>
        </form>

        <form className="card p-4 space-y-3" onSubmit={onPayout}>
          <div className="font-medium text-sm">Выплата менеджеру</div>
          <p className="text-[12px] text-ink/55">
            Когда отдали деньги наличкой или переводом — зафиксируйте здесь. Баланс «к выплате» обнулится (или уменьшится).
          </p>
          <div>
            <label className="label">Способ</label>
            <div className="mt-1 flex flex-wrap gap-2">
              {(
                [
                  ['cash', 'Наличка'],
                  ['transfer', 'Перевод'],
                  ['other', 'Другое'],
                ] as const
              ).map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  className={payoutMethod === v ? 'btn-primary' : 'btn-ghost'}
                  onClick={() => setPayoutMethod(v)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label">Сумма, ₽ (пусто = весь баланс {formatKop(mgr.pendingKopecks)})</label>
            <input
              className="input mt-1"
              type="number"
              min={0}
              step={0.01}
              placeholder={(mgr.pendingKopecks / 100).toString()}
              value={payoutRub}
              onChange={(e) => setPayoutRub(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Комментарий</label>
            <input
              className="input mt-1"
              placeholder="Чек, ФИО, банк…"
              value={payoutComment}
              onChange={(e) => setPayoutComment(e.target.value)}
            />
          </div>
          <button type="submit" className="btn-primary w-full" disabled={busy || mgr.pendingKopecks <= 0}>
            Отметить выплаченным
          </button>
        </form>

        <div className="card lg:col-span-2 p-4 space-y-3">
          <div className="font-medium text-sm">Привязать заведение</div>
          <div className="relative max-w-xl">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
            <input
              className="input !pl-9"
              placeholder="Поиск по названию, email владельца…"
              value={tenantQ}
              onChange={(e) => setTenantQ(e.target.value)}
            />
          </div>
          {tenantSearching ? <div className="text-xs text-ink/45">Ищем…</div> : null}
          {tenantHits.length > 0 ? (
            <ul className="divide-y divide-line border border-line">
              {tenantHits.map((t) => {
                const already = attachedIds.has(t.id);
                const takenBy = !already && t.referrer ? t.referrer : null;
                return (
                  <li key={t.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{t.name}</div>
                      <div className="text-xs text-ink/45 truncate">
                        {t.ownerEmail ?? t.ownerName ?? t.city ?? t.id}
                      </div>
                      {takenBy ? (
                        <div className="mt-0.5 text-[11px] text-amber-600">
                          Уже у {takenBy.name} ({takenBy.code}) — привязка перепишется
                        </div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="btn-ghost shrink-0"
                      disabled={busy || already || mgr.status !== 'active'}
                      onClick={() => void attachTenant(t)}
                    >
                      <Link2 size={14} />
                      {already ? 'Уже здесь' : 'Привязать'}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : tenantQ.trim().length >= 2 && !tenantSearching ? (
            <div className="text-xs text-ink/45">Ничего не найдено</div>
          ) : null}
        </div>

        <div className="card lg:col-span-2 overflow-x-auto">
          <div className="px-4 py-3 border-b border-line font-medium text-sm">
            Заведения ({mgr.establishments.length})
          </div>
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-ink/50 border-b border-line">
                <th className="px-4 py-2">Название</th>
                <th className="px-4 py-2">Тариф</th>
                <th className="px-4 py-2">Ставка</th>
                <th className="px-4 py-2">Привязка</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {mgr.establishments.map((e) => (
                <tr key={e.id} className="border-b border-line/60">
                  <td className="px-4 py-2">
                    <Link to={`/tenants/${e.id}`} className="hover:text-orange">{e.name}</Link>
                  </td>
                  <td className="px-4 py-2">{e.subscription?.plan ?? '—'}</td>
                  <td className="px-4 py-2">{e.commissionBps != null ? `${e.commissionBps / 100}%` : '—'}</td>
                  <td className="px-4 py-2 text-xs text-ink/55">{e.attachedAt ? formatDate(e.attachedAt) : '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      className="btn-ghost text-red-600"
                      disabled={busy}
                      onClick={() => void detachTenant(e.id, e.name)}
                    >
                      <Unlink size={14} /> Отвязать
                    </button>
                  </td>
                </tr>
              ))}
              {!mgr.establishments.length ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-ink/45">
                    Пока нет привязанных заведений
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="card lg:col-span-2 overflow-x-auto">
          <div className="px-4 py-3 border-b border-line font-medium text-sm">
            История выплат ({mgr.payouts?.length ?? 0})
          </div>
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="text-left text-ink/50 border-b border-line">
                <th className="px-4 py-2">Дата</th>
                <th className="px-4 py-2">Сумма</th>
                <th className="px-4 py-2">Способ</th>
                <th className="px-4 py-2">Комментарий</th>
              </tr>
            </thead>
            <tbody>
              {(mgr.payouts ?? []).map((p) => (
                <tr key={p.id} className="border-b border-line/60">
                  <td className="px-4 py-2 text-xs text-ink/55">{formatDate(p.createdAt)}</td>
                  <td className="px-4 py-2 font-medium">{formatKop(p.amountKopecks)}</td>
                  <td className="px-4 py-2">
                    {p.method === 'cash' ? 'Наличка' : p.method === 'transfer' ? 'Перевод' : 'Другое'}
                  </td>
                  <td className="px-4 py-2 text-xs text-ink/55">{p.comment || '—'}</td>
                </tr>
              ))}
              {!(mgr.payouts ?? []).length ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-ink/45">
                    Выплат ещё не было
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="lg:col-span-2 space-y-3">
          <div className="font-medium text-sm">Аудит действий в кабинете</div>
          <ManagerAuditPanel managerId={mgr.id} />
        </div>
      </div>
    </div>
  );
}
